import * as React from "react";
import { quoteApi, requestApi } from "@/boxhub-api";
import { Constants } from "@/api/constants";
import { DeclineReason, QuoteDTO, RequestActions, RequestDTO } from "@/api/data-contracts";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import { CancellationReasonDialog } from "@/common/components/CancellationReasonDialog";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import DeclineReasonSelect from "@/common/request-cards/DeclineReasonSelect";
import {isDismissConfirmable, toDismissQuoteBody} from "@/common/request-details/dismiss-dialog-logic";
import ExtendDatesDialog from "@/pages/internal-request-page/ExtendDatesDialog";
import EditAdditionalInformationDialog from "@/pages/internal-request-page/EditAdditionalInformationDialog";
import QuoteMatrixDialog from "@/pages/internal-request-page/QuoteMatrixDialog";
import EmailHistoryDialog from "@/common/EmailHistoryDialog";
import { useTranslation } from "react-i18next";
import { CommonI18N, RequestI18N } from "@/translations";
import { type ShowSnackbarProps } from "@/lib/snackbarex";
import { type ModelAction } from "@/lib/modelstate";
import { type DialogFunction } from "@/lib/dialog-controller";
import { formatCustomerRequestNumber } from "@/model/request";

interface UseRequestDialogsParams {
    model: RequestDTO;
    dispatch: (action: ModelAction) => void;
    showDialog: (factory: DialogFunction) => void;
    showMessage: (opts: ShowSnackbarProps) => void;
    quote: QuoteDTO | null;
    quotesByCompany: QuoteDTO[][];
    setCompanyIdx: (idx: number) => void;
    setVersionIdx: (idx: number) => void;
    navigateToOverview: (forceLeave: boolean) => void;
    setProcessing: (processing: boolean) => void;
    isOperator: boolean;
}

export interface RequestDialogHandlers {
    handleDeleteRequest: () => void;
    handleUnpublishRequest: () => void;
    handleCancelRequest: () => void;
    handleConfirmQuote: () => void;
    handleDismissQuote: () => void;
    handleGiveFeedback: () => void;
    handleOpenMatrix: () => void;
    handleStartExtendDates: () => void;
    handleOpenHistoryDialog: () => void;
    handleEditAdditionalInformation: () => void;
    handleResendPublishNotification: () => void;
    handleConfirmInternally: () => void;
}

/**
 * Locates the company and version index of the given quote within the grouped quotes matrix.
 * Returns null when the quote is not present. Extracted to module scope to keep the matrix
 * selection handler shallow (avoids deep function nesting).
 */
function findQuotePosition(
    quotesByCompany: QuoteDTO[][],
    quoteId: number | undefined
): { companyIdx: number; versionIdx: number } | null {
    const companyIdx = quotesByCompany.findIndex((versions) => versions.some((q) => q.id === quoteId));
    if (companyIdx === -1) {
        return null;
    }
    const versionIdx = quotesByCompany[companyIdx].findIndex((q) => q.id === quoteId);
    return { companyIdx, versionIdx: versionIdx !== -1 ? versionIdx : 0 };
}

/**
 * Dialog for rejecting a quote with a structured decline reason.
 * Reuses the shared {@link DeclineReasonSelect} (RadioGroup over all seven reasons plus the dynamic
 * optional/mandatory free-text field) and the pure {@link isDismissConfirmable}/{@link toDismissQuoteBody}
 * logic. Extracted to module scope to avoid unmount/remount on each render.
 */
const RejectQuoteDialog: React.FC<{
    open: boolean;
    close: () => void;
    quoteId: number;
    showMessage: (opts: ShowSnackbarProps) => void;
    dispatch: (action: ModelAction) => void;
    t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ open, close, quoteId, showMessage, dispatch, t }) => {
    const [reason, setReason] = React.useState<DeclineReason | undefined>(undefined);
    const [freeText, setFreeText] = React.useState<string | null>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const canConfirm = isDismissConfirmable({ reason, freeText });

    return (
        <ConfirmationDialog
            open={open}
            onClose={close}
            autoFocusConfirm={false}
            onConfirm={async () => {
                if (!canConfirm || reason === undefined || submitting) {
                    return;
                }
                try {
                    setSubmitting(true);
                    const response = await quoteApi.dismissQuote(quoteId, toDismissQuoteBody(reason, freeText));
                    showMessage({
                        summary: t(RequestI18N.details.message.quoteDismissed),
                        severity: "success"
                    });
                    dispatch({ type: "--set", data: response.data, nochange: true });
                    close();
                } catch (error: unknown) {
                    showMessage({ summary: t(CommonI18N.message.unknownError), severity: "error", error });
                } finally {
                    setSubmitting(false);
                }
            }}
            title={t(RequestI18N.details.dialog.dismissQuoteTitle)}
            message={t(RequestI18N.details.dialog.rejectQuoteMessage)}
            confirmButtonText={t(RequestI18N.details.action.confirm)}
            cancelButtonText={t(CommonI18N.action.cancel)}
            dataCy="reject-quote-dialog"
            confirmDisabled={!canConfirm || submitting}
        >
            <div style={{ marginTop: 12 }}>
                <DeclineReasonSelect
                    reason={reason}
                    freeText={freeText}
                    onReasonChange={setReason}
                    onFreeTextChange={setFreeText}
                    freeTextFieldName="dismiss-quote-free-text"
                />
            </div>
        </ConfirmationDialog>
    );
};

/**
 * Dialog for giving feedback on a quote.
 * Extracted to module scope to avoid unmount/remount on each render.
 */
const GiveFeedbackDialog: React.FC<{
    open: boolean;
    close: () => void;
    quoteId: number;
    showMessage: (opts: ShowSnackbarProps) => void;
    dispatch: (action: ModelAction) => void;
    t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ open, close, quoteId, showMessage, dispatch, t }) => {
    const [feedback, setFeedback] = React.useState<string>("");
    const [submitting, setSubmitting] = React.useState(false);
    const trimmed = feedback.trim();
    const isTooLong = feedback.length > Constants.FEEDBACK_LENGTH_MAX;
    const isValid = trimmed.length >= Constants.FEEDBACK_LENGTH_MIN && !isTooLong;
    let feedbackError: { message: string } | undefined;
    if (isTooLong) {
        feedbackError = {
            message: t(RequestI18N.form.validation.feedbackMaxLength, { max: Constants.FEEDBACK_LENGTH_MAX })
        };
    } else if (trimmed.length === 0 && feedback.length > 0) {
        feedbackError = { message: t(RequestI18N.form.validation.feedbackRequired) };
    }
    return (
        <ConfirmationDialog
            open={open}
            onClose={close}
            autoFocusConfirm={false}
            onConfirm={async () => {
                if (!isValid || submitting) {
                    return;
                }
                try {
                    setSubmitting(true);
                    const response = await quoteApi.giveFeedback(quoteId, { feedback: trimmed });
                    showMessage({
                        summary: t(RequestI18N.details.message.giveFeedbackSuccess),
                        severity: "success"
                    });
                    dispatch({ type: "--set", data: response.data, nochange: true });
                    close();
                } catch (error: unknown) {
                    showMessage({ summary: t(CommonI18N.message.unknownError), severity: "error", error });
                } finally {
                    setSubmitting(false);
                }
            }}
            title={t(RequestI18N.details.dialog.giveFeedbackTitle)}
            message={t(RequestI18N.details.dialog.giveFeedbackMessage)}
            confirmButtonText={t(RequestI18N.details.action.confirm)}
            cancelButtonText={t(CommonI18N.action.cancel)}
            confirmDisabled={!isValid || submitting}
        >
            <div style={{ marginTop: 12 }}>
                <StringInputField
                    multiline
                    minRows={4}
                    label={t(RequestI18N.details.field.feedback)}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    maxChars={Constants.FEEDBACK_LENGTH_MAX}
                    error={feedbackError}
                />
            </div>
        </ConfirmationDialog>
    );
};

/**
 * Builds the validation error for a reason input field without nesting ternaries.
 * Returns the max-length error when the input is too long, the required error when the
 * field must not be empty, otherwise undefined.
 */
const buildReasonError = (
    t: (key: string, opts?: Record<string, unknown>) => string,
    isTooLong: boolean,
    showRequired: boolean,
    maxMessageKey: string,
    requiredMessageKey: string,
    max: number
): { message: string } | undefined => {
    if (isTooLong) {
        return { message: t(maxMessageKey, { max }) };
    }
    if (showRequired) {
        return { message: t(requiredMessageKey) };
    }
    return undefined;
};

/**
 * Dialog for internally confirming a deactivated request with a mandatory reason (operator only).
 * Extracted to module scope to avoid unmount/remount on each render.
 */
const ConfirmInternallyDialog: React.FC<{
    open: boolean;
    close: () => void;
    requestId: number;
    model: RequestDTO;
    showMessage: (opts: ShowSnackbarProps) => void;
    dispatch: (action: ModelAction) => void;
    t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ open, close, requestId, model, showMessage, dispatch, t }) => {
    const [reason, setReason] = React.useState<string>("");
    const [submitting, setSubmitting] = React.useState(false);
    const trimmed = reason.trim();
    const isTooLong = reason.length > Constants.INTERNAL_CONFIRMATION_REASON_LENGTH_MAX;
    const isValid = trimmed.length >= Constants.INTERNAL_CONFIRMATION_REASON_LENGTH_MIN && !isTooLong;
    return (
        <ConfirmationDialog
            open={open}
            onClose={close}
            autoFocusConfirm={false}
            onConfirm={async () => {
                if (!isValid || submitting) {
                    return;
                }
                try {
                    setSubmitting(true);
                    const response = await requestApi.confirmRequestInternally(requestId, { reason: trimmed });
                    const requestNumber = formatCustomerRequestNumber(model) ?? String(model.id);
                    showMessage({
                        summary: t(RequestI18N.details.message.confirmInternallySuccess, { requestNumber }),
                        severity: "success"
                    });
                    dispatch({ type: "--set", data: response.data, nochange: true });
                    close();
                } catch (error: unknown) {
                    showMessage({ summary: t(CommonI18N.message.unknownError), severity: "error", error });
                } finally {
                    setSubmitting(false);
                }
            }}
            title={t(RequestI18N.details.dialog.confirmInternallyTitle)}
            message={t(RequestI18N.details.dialog.confirmInternallyMessage)}
            confirmButtonText={t(RequestI18N.details.action.confirm)}
            cancelButtonText={t(CommonI18N.action.cancel)}
            dataCy="confirm-internally-dialog"
            maxWidth="sm"
            fullWidth
            confirmDisabled={!isValid || submitting}
        >
            <div style={{ marginTop: 12 }}>
                <StringInputField
                    multiline
                    minRows={3}
                    label={t(RequestI18N.details.field.confirmInternallyReason)}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxChars={Constants.INTERNAL_CONFIRMATION_REASON_LENGTH_MAX}
                    helperText={`${reason.length} / ${Constants.INTERNAL_CONFIRMATION_REASON_LENGTH_MAX}`}
                    error={buildReasonError(
                        t,
                        isTooLong,
                        trimmed.length === 0 && reason.length > 0,
                        RequestI18N.form.validation.confirmInternallyReasonMax,
                        RequestI18N.form.validation.confirmInternallyReasonRequired,
                        Constants.INTERNAL_CONFIRMATION_REASON_LENGTH_MAX
                    )}
                />
            </div>
        </ConfirmationDialog>
    );
};

/**
 * Extracts all dialog-based handlers from the InternalRequestPage.
 * Each handler opens a dialog via showDialog and performs an API call.
 */
export function useRequestDialogs({
    model,
    dispatch,
    showDialog,
    showMessage,
    quote,
    quotesByCompany,
    setCompanyIdx,
    setVersionIdx,
    navigateToOverview,
    setProcessing,
    isOperator
}: UseRequestDialogsParams): RequestDialogHandlers {
    const { t } = useTranslation();

    const handleDeleteRequest = () => {
        const modelId = model?.id;
        if (modelId != null) {
            showDialog((open, close) => (
                <ConfirmationDialog
                    open={open}
                    onClose={close}
                    onConfirm={async () => {
                        try {
                            setProcessing(true);
                            await requestApi.deleteRequest(modelId);
                            showMessage({ summary: t(RequestI18N.details.message.deleteSuccess), severity: "success" });
                            navigateToOverview(true);
                            close();
                        } catch (error: unknown) {
                            showMessage({
                                summary: t(RequestI18N.details.message.deleteFailed),
                                severity: "error",
                                error
                            });
                        } finally {
                            setProcessing(false);
                            close();
                        }
                    }}
                    title={t(RequestI18N.details.dialog.confirmDeleteTitle)}
                    message={`${t(RequestI18N.details.dialog.deleteMessagePartOne)} ${model?.boxhubNumber ?? t(CommonI18N.message.notGiven)} ${t(RequestI18N.details.dialog.deleteMessagePartTwo)}`}
                    confirmButtonText={t(RequestI18N.details.action.delete)}
                    cancelButtonText={t(CommonI18N.action.cancel)}
                    maxWidth="sm"
                    fullWidth
                />
            ));
        }
    };

    const handleUnpublishRequest = () => {
        showDialog((open, close) => (
            <CancellationReasonDialog
                open={open}
                onClose={close}
                onConfirm={async (cancellationReason) => {
                    try {
                        await requestApi.unpublishRequest(model.id!, { cancellationReason });
                        showMessage({
                            summary: t(RequestI18N.details.message.withdrawConfirmed),
                            severity: "success"
                        });
                        navigateToOverview(true);
                    } catch (error: unknown) {
                        showMessage({ summary: t(CommonI18N.message.unknownError), severity: "error", error });
                    } finally {
                        close();
                    }
                }}
                title={t(RequestI18N.details.dialog.withdrawRequestTitle)}
                message={t(RequestI18N.details.dialog.withdrawRequestMessage)}
                confirmButtonText={t(RequestI18N.details.action.confirm)}
                cancelButtonText={t(CommonI18N.action.cancel)}
                reasonLabel={t(RequestI18N.details.field.withdrawalReason)}
                reasonPlaceholder={t(RequestI18N.details.field.withdrawalReasonPlaceholder)}
            />
        ));
    };

    const handleCancelRequest = () => {
        showDialog((open, close) => (
            <CancellationReasonDialog
                open={open}
                onClose={close}
                onConfirm={async (cancellationReason) => {
                    try {
                        if (model.allowedActions?.includes(RequestActions.CANCEL_REQUEST)) {
                            await requestApi.cancelRequest(model.id!, { cancellationReason });
                        } else if (model.allowedActions?.includes(RequestActions.CANCEL_PREVIOUSLY_PUBLISHED_REQUEST)) {
                            await requestApi.cancelPreviouslyPublishedRequest(model.id!, {
                                cancellationReason
                            });
                        } else {
                            throw new Error("Unknown Allowed Action for Cancel");
                        }
                        showMessage({
                            summary: t(RequestI18N.details.message.cancelConfirmedNotification),
                            severity: "success"
                        });
                        navigateToOverview(true);
                    } catch (error: unknown) {
                        showMessage({ summary: t(CommonI18N.message.unknownError), severity: "error", error });
                    } finally {
                        close();
                    }
                }}
                title={t(RequestI18N.details.dialog.confirmCancelRequestTitle)}
                message={t(RequestI18N.details.dialog.confirmCancelRequestMessage)}
                confirmButtonText={t(RequestI18N.details.action.confirm)}
                cancelButtonText={t(CommonI18N.action.cancel)}
            />
        ));
    };

    const handleConfirmQuote = () => {
        if (quote != null) {
            if (quote.orderData.quantityOptionSelected == null) {
                showMessage({ summary: t(RequestI18N.details.message.selectQuoteError), severity: "error" });
                return;
            }
            showDialog((open, close) => (
                <ConfirmationDialog
                    open={open}
                    onClose={close}
                    onConfirm={async () => {
                        try {
                            await quoteApi.confirmQuote(quote.id!, {
                                quantityOptionUsed: quote.orderData.quantityOptionSelected!
                            });
                            showMessage({
                                summary: t(RequestI18N.details.message.quoteConfirmed),
                                severity: "success"
                            });
                            navigateToOverview(true);
                        } catch (error: unknown) {
                            showMessage({ summary: t(CommonI18N.message.unknownError), severity: "error", error });
                        } finally {
                            close();
                        }
                    }}
                    title={t(RequestI18N.details.dialog.confirmQuoteTitle)}
                    message={t(RequestI18N.details.dialog.confirmQuoteMessage)}
                    confirmButtonText={t(RequestI18N.details.action.confirm)}
                    cancelButtonText={t(CommonI18N.action.cancel)}
                />
            ));
        }
    };

    const handleDismissQuote = () => {
        if (quote?.id == null) {
            return;
        }
        const quoteId = quote.id;
        showDialog((open, close) => (
            <RejectQuoteDialog
                open={open}
                close={close}
                quoteId={quoteId}
                showMessage={showMessage}
                dispatch={dispatch}
                t={t}
            />
        ));
    };

    const handleGiveFeedback = () => {
        if (quote?.id == null) {
            return;
        }
        const quoteId = quote.id;
        showDialog((open, close) => (
            <GiveFeedbackDialog
                open={open}
                close={close}
                quoteId={quoteId}
                showMessage={showMessage}
                dispatch={dispatch}
                t={t}
            />
        ));
    };

    const handleOpenMatrix = () => {
        showDialog((open, close) => (
            <QuoteMatrixDialog
                open={open}
                close={close}
                model={model}
                setQuote={(selectedQuote) => {
                    const position = findQuotePosition(quotesByCompany, selectedQuote.id);
                    if (position) {
                        setCompanyIdx(position.companyIdx);
                        setVersionIdx(position.versionIdx);
                    }
                }}
            />
        ));
    };

    const handleStartExtendDates = () => {
        showDialog((open, close) => (
            <ExtendDatesDialog
                open={open}
                onClose={close}
                model={model}
                onSuccess={(updatedRequest) => {
                    dispatch({ type: "--set", data: updatedRequest, nochange: true });
                }}
            />
        ));
    };

    const handleOpenHistoryDialog = () => {
        const requestId = model?.id;
        if (requestId) {
            showDialog((open, close) => <EmailHistoryDialog open={open} requestId={requestId} onClose={close} />);
        }
    };

    const handleResendPublishNotification = () => {
        showDialog((open, close) => (
            <ConfirmationDialog
                open={open}
                onClose={close}
                onConfirm={async () => {
                    try {
                        await requestApi.resendPublishNotification(model.id!);
                        showMessage({
                            summary: t(RequestI18N.details.message.resendPublishNotificationSuccess),
                            severity: "success"
                        });
                    } catch (error: unknown) {
                        showMessage({ summary: t(CommonI18N.message.unknownError), severity: "error", error });
                    } finally {
                        close();
                    }
                }}
                title={t(RequestI18N.details.dialog.resendPublishNotificationTitle)}
                message={t(RequestI18N.details.dialog.resendPublishNotificationMessage)}
                confirmButtonText={t(RequestI18N.details.action.confirm)}
                cancelButtonText={t(CommonI18N.action.cancel)}
            />
        ));
    };

    const handleEditAdditionalInformation = () => {
        showDialog((open, close) => (
            <EditAdditionalInformationDialog
                open={open}
                onClose={close}
                model={model}
                onSuccess={(updatedRequest) => {
                    dispatch({ type: "--set", data: updatedRequest, nochange: true });
                }}
            />
        ));
    };

    const handleConfirmInternally = () => {
        const requestId = model?.id;
        if (requestId == null) {
            return;
        }
        showDialog((open, close) => (
            <ConfirmInternallyDialog
                open={open}
                close={close}
                requestId={requestId}
                model={model}
                showMessage={showMessage}
                dispatch={dispatch}
                t={t}
            />
        ));
    };

    return {
        handleDeleteRequest,
        handleUnpublishRequest,
        handleCancelRequest,
        handleConfirmQuote,
        handleDismissQuote,
        handleGiveFeedback,
        handleOpenMatrix,
        handleStartExtendDates,
        handleOpenHistoryDialog,
        handleEditAdditionalInformation,
        handleResendPublishNotification,
        handleConfirmInternally
    };
}
